import { Component } from '@theme/component';
 
/**
 * =====================================================================
 * LIBERAÇÃO DO LABORATÓRIO
 * ---------------------------------------------------------------------
 * Controla o que fica visível no wizard enquanto aguardamos a liberação
 * de alguns tipos de lente. Nada foi apagado do código: quando o lab
 * liberar, é só ajustar aqui.
 *
 *  - coresSolarPermitidas: cores de lente solar que aparecem no wizard.
 *    Use as mesmas chaves de LENS_COLOR_KEYS (mais abaixo).
 *    Deixe a lista vazia [] para liberar TODAS as cores.
 *
 *  - mostrarSolarComGrau: false esconde a opção "solar com grau" nos
 *    produtos solares. true mostra de novo.
 * =====================================================================
 */
const LENS_CONFIG = {
  coresSolarPermitidas: [
    'cinza-black', 'cinza-black-degrade',
    'marrom-dark', 'marrom-dark-degrade',
    'g15', 'g15-degrade',
  ],
  mostrarSolarComGrau: false,
};
 
/**
 * Diz se uma cor de lente solar deve aparecer no wizard, de acordo com
 * LENS_CONFIG.coresSolarPermitidas (lista vazia = todas liberadas).
 * @param {string} colorKey
 */
function isLensColorEnabled(colorKey) {
  const allowed = LENS_CONFIG.coresSolarPermitidas;
  return !allowed || allowed.length === 0 || allowed.includes(colorKey);
}
 
/**
 * Registro de todas as etapas "normais" do wizard (não placeholders).
 * Cada etapa tem:
 *  - title: título exibido no header
 *  - subtitle: texto de aviso opcional, exibido abaixo do título
 *  - propertyKey: nome da property gravada no pedido quando uma opção dessa etapa é escolhida
 *  - options: lista de opções, cada uma com:
 *      - label: texto do botão
 *      - price: texto de preço exibido à direita (opcional)
 *      - description: texto explicativo abaixo do botão
 *      - type: 'terminal' (finaliza e adiciona ao carrinho) ou 'next' (avança pra outra etapa)
 *      - next: id da próxima etapa (obrigatório quando type é 'next')
 */
/**
 * Reimplementação do filtro Liquid `handleize` do Shopify (minúsculas,
 * espaços/símbolos viram um único hífen, sem hífen no início/fim). Usada
 * pra montar a mesma chave que o Liquid gera no mapa de preview de cores
 * (data-lens-preview-map), já que ali o valor da opção de cor da armação
 * passa por `| handleize`.
 * @param {string} value
 */
function handleize(value) {
  return (value || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
 
/**
 * IDs das variantes dos produtos "complemento" ocultos, usados para somar
 * o preço dos upgrades como uma segunda linha no carrinho.
 */
const ADDON_VARIANTS = {
  'grau-leve': '48582923124906',
  'grau-moderado': '48582923354282',
  'grau-alto': '48582923944106',
  'filtro-luz-azul': '48582924501162',
  'sem-grau-filtro': '48582924501162', // mesma variante do filtro de luz azul
  'filtro-luz-azul-premium': '48823564796074',
};
 
/**
 * Cores de lente solar disponíveis, na ordem em que aparecem no wizard.
 * A chave (colorKey) precisa bater exatamente com o trecho final do nome
 * dos arquivos enviados em Conteúdo > Arquivos, ex: cora-black-matte-g15.png
 * -> colorKey "g15".
 * (Quais delas aparecem de fato é controlado por LENS_CONFIG, no topo.)
 */
const LENS_COLOR_KEYS = [
  'cinza-black', 'cinza-black-degrade',
  'marrom-dark', 'marrom-dark-degrade',
  'marrom-lll', 'marrom-lll-degrade',
  'marrom-ll', 'marrom-ll-degrade',
  'marrom-l', 'marrom-l-degrade',
  'verde', 'verde-degrade',
  'pink', 'pink-degrade',
  'azul', 'azul-degrade',
  'g15', 'g15-degrade',
];
 
/** Texto exibido embaixo de cada bolinha de cor. Ajuste à vontade. */
const LENS_COLOR_LABELS = {
  'cinza-black': 'cinza black',
  'cinza-black-degrade': 'cinza black degradê',
  'marrom-dark': 'marrom dark',
  'marrom-dark-degrade': 'marrom dark degradê',
  'marrom-lll': 'marrom III',
  'marrom-lll-degrade': 'marrom III degradê',
  'marrom-ll': 'marrom II',
  'marrom-ll-degrade': 'marrom II degradê',
  'marrom-l': 'marrom I',
  'marrom-l-degrade': 'marrom I degradê',
  verde: 'verde',
  'verde-degrade': 'verde degradê',
  pink: 'pink',
  'pink-degrade': 'pink degradê',
  azul: 'azul',
  'azul-degrade': 'azul degradê',
  g15: 'g15',
  'g15-degrade': 'g15 degradê',
};
 
/**
 * Cor sólida de cada lente (hex exato, extraído das fotos reais da CORA).
 * Usado só pra pintar a bolinha do botão — não tem relação com o preview
 * de imagem, que vem do mapa gerado no Liquid.
 */
const LENS_COLOR_HEX = {
  'cinza-black': '#201A31',
  'marrom-dark': '#3B1E1A',
  'marrom-lll': '#7B4F31',
  'marrom-ll': '#B68C62',
  'marrom-l': '#BFA99B',
  verde: '#375144',
  pink: '#D84077',
  azul: '#6086C4',
  g15: '#151D19',
};
 
/** Topo/base do degradê de cada lente (mesmo critério do hex sólido acima). */
const LENS_COLOR_GRADIENT = {
  'cinza-black': ['#221C32', '#CDC9D1'],
  'marrom-dark': ['#3C1F1B', '#D7CECC'],
  'marrom-lll': ['#82583B', '#F4EFEC'],
  'marrom-ll': ['#BB936B', '#F9F6F2'],
  'marrom-l': ['#C4AEA2', '#FAF8F7'],
  verde: ['#42594D', '#EDEFED'],
  pink: ['#D84077', '#FDF6F9'],
  azul: ['#6287C5', '#EEF2F9'],
  g15: ['#171E1A', '#CDCCCC'],
};
 
/**
 * Gera a etapa de intensidade do grau, parametrizada pela próxima etapa
 * (no fluxo normal vai para "tratamento"; no fluxo solar vai para "solar-cor").
 * @param {string} nextStepId
 */
function buildGrauIntensidadeStep(nextStepId) {
  return {
    title: 'Escolha seu tipo de grau',
    propertyKey: 'grau',
    options: [
      {
        id: 'grau-leve',
        label: 'grau leve',
        price: '+R$ 150,00',
        description:
          'Lentes em acrílico (índice 1.50) com espessura padrão, indicado para graus leves. Esférico -2,00 à +2,00 e cilíndrico até -2,00.',
        type: 'next',
        next: nextStepId,
        prescriptionInfo: {
          od: { esf: '-1,50', cil: '-0,75', eixo: '90°' },
          oe: { esf: '-1,75', cil: '-1,00', eixo: '85°' },
          caption: 'válido para esférico até +/- 2,75 e cilíndrico até -2,00',
        },
      },
      {
        id: 'grau-moderado',
        label: 'grau moderado',
        price: '+R$ 220,00',
        description:
          'Lentes em policarbonato (índice 1.59), mais leves e resistentes, indicadas para graus moderados. Esférico de -4,00 à +4,00 e cilíndrico até -3,00.',
        type: 'next',
        next: nextStepId,
        prescriptionInfo: {
          od: { esf: '-3,00', cil: '-1,50', eixo: '100°' },
          oe: { esf: '-3,25', cil: '-1,25', eixo: '95°' },
          caption: 'válido para esférico até +/- 4,00 e cilíndrico até -2,00',
        },
      },
      {
        id: 'grau-alto',
        label: 'grau alto',
        price: '+R$ 280,00',
        description:
          'Lentes em resina (índice 1.61), com espessura mais fina e mais leve, indicadas para graus mais altos. Esférico de -6,00 à +6,00 e cilíndrico até -4,00.',
        type: 'next',
        next: nextStepId,
        prescriptionInfo: {
          od: { esf: '-6,00', cil: '-3,50', eixo: '70°' },
          oe: { esf: '+5,50', cil: '-3,00', eixo: '75°' },
          caption: 'válido para esférico até +6,00 (hipermetropia) ou -6,50 (miopia), e cilíndrico até -4,00',
        },
      },
    ],
  };
}
 
const STEPS = {
  'tipo-grau': {
    title: 'Escolha seu tipo de lente',
    propertyKey: 'tipo de lente',
    options: [
      {
        id: 'com-grau',
        label: 'com grau',
        description: 'Lentes monofocais para correção de miopia, hipermetropia ou astigmatismo (com opção de adicionar filtro de luz azul).',
        type: 'next',
        next: 'grau-intensidade',
      },
      {
        id: 'sem-grau-filtro',
        label: 'sem grau | com filtro de luz azul',
        description:
          'Lentes sem grau ideais para quem está à procura de estilo e mais conforto visual, perfeito para quem não desgruda os olhos das telas.',
        type: 'next',
        next: 'resumo',
      },
      {
        id: 'sem-grau-armacao',
        label: 'sem grau | apenas a armação',
        description:
          'Armação acompanhada de lentes de demonstração feitas de acrílico e não recomendadas para o uso diário.',
        type: 'next',
        next: 'resumo',
      },
    ],
  },
  'tipo-solar': {
    title: 'escolha seu tipo de lente',
    propertyKey: 'tipo de lente',
    options: [
      {
        id: 'solar',
        label: 'solar',
        description: 'Lentes solares que proporcionam o bloqueio de 100% dos raios uv, além de serem resistente a arranhões e excelente conforto visual.',
        type: 'next',
        next: 'solar-cor',
      },
      {
        id: 'solar-com-grau',
        label: 'solar com grau',
        description: 'Lentes solares monofocais para correção de miopia, hipermetropia ou astigmatismo, proporcionam o bloqueio de 100% dos raios uv, além de serem resistente a arranhões e excelente conforto visual. ',
        type: 'next',
        next: 'grau-intensidade-solar',
      },
    ].filter((option) => LENS_CONFIG.mostrarSolarComGrau || option.id !== 'solar-com-grau'),
  },
  'grau-intensidade': buildGrauIntensidadeStep('tratamento'),
  'grau-intensidade-solar': buildGrauIntensidadeStep('solar-cor'),
  'solar-cor': {
    title: 'Escolha a cor da lente',
    propertyKey: 'cor da lente',
    colorPicker: true,
    options: LENS_COLOR_KEYS.filter(isLensColorEnabled).map((key) => ({
      id: `cor-${key}`,
      label: LENS_COLOR_LABELS[key] || key,
      colorKey: key,
      type: 'next',
      next: 'resumo',
    })),
  },
  tratamento: {
    title: 'Escolha seu tipo de tratamento',
    propertyKey: 'tratamento',
    options: [
      {
        id: 'classico',
        label: 'clássico',
        price: 'grátis',
        description: 'Lentes padrão com antireflexo e proteção básica.',
        type: 'next',
        next: 'resumo',
      },
      {
        id: 'filtro-luz-azul',
        label: 'clássico | filtro de luz azul',
        price: '+R$ 150,00',
        description:
          'Além da resistência e proteção uv, também filtram mais luz azul de aparelhos digitais e do sol do que as lentes clássicas. *o filtro de luz azul pode deixar um residual azulado ou esverdeado nas lentes.',
        type: 'next',
        next: 'resumo',
      },
      {
      id: 'filtro-luz-azul-premium',
      label: 'premium | filtro de luz azul',
      price: '+R$ 180,00',
      description:
        'Lentes de linha premium, com acabamento superior e maior durabilidade, alta resistência, antirreflexo e proteção uv, também filtram mais luz azul de aparelhos digitais e do sol do que as lentes clássicas. *o filtro de luz azul pode deixar um residual azulado ou esverdeado nas lentes.',
      type: 'next',
      next: 'resumo',
      },
    ],
    banner: {
      title: 'olhos cansados de tantas telas?',
      body: 'aproveite o filtro de luz azul e reduza o estresse causado pela luz emitida por aparelhos digitais.',
    },
  },
  resumo: {
    title: 'Ótima escolha! Suas lentes estão selecionadas',
    subtitle:
      'Assim que finalizar a compra vamos pedir os dados da sua receita. não se preocupe, também vamos te lembrar via e-mail e sms!',
    summary: true,
  },
};
 
/**
 * Etapas que ainda não foram construídas — exibem só uma mensagem.
 * (vazio por enquanto, todas as etapas já foram construídas)
 */
const PLACEHOLDER_STEPS = {};
 
/** Junta tudo num único registro pra facilitar o lookup por id. */
const ALL_STEPS = { ...STEPS, ...PLACEHOLDER_STEPS };
 
/** Qual etapa inicial usar de acordo com a tag do produto. */
const ENTRY_STEP_BY_TAG = {
  'tipo-solar': 'tipo-solar',
  'tipo-grau': 'tipo-grau',
};
 
/**
 * Wizard de seleção de lentes, exibido dentro de um <dialog-component> no carrinho.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} title
 * @property {HTMLElement} body
 * @property {HTMLButtonElement} backButton
 *
 * @extends {Component<Refs>}
 */
class LensSelectorComponent extends Component {
  requiredRefs = ['title', 'body', 'backButton', 'previewArea', 'footerArea'];
 
  /** @type {HTMLFormElement | null} */
  #form = null;
 
  /** @type {string[]} ids das etapas visitadas, na ordem (pra navegação de voltar) */
  #stack = [];
 
  /** @type {Array<{key: string, label: string}>} seleções acumuladas até agora */
  #selections = [];
 
  /** @type {Array<{variantId: string, label: string} | null>} complementos pagos, em paralelo a #selections */
  #addons = [];
 
  /** @type {Array<{alt: string, src: string}>} lista de imagens do produto (alt + url) */
  #mediaList = [];
 
  /** @type {string} cor da armação selecionada no momento, normalizada (formato handleize, ex: "black-matte") */
  #frameColorSlug = '';
 
  /** @type {Record<string, Record<string, string | null>>} mapa {cor-armação: {cor-lente: url}} vindo do Liquid */
  #lensPreviewMap = {};
 
  /** @type {string} imagem padrão do produto (sem cor de lente selecionada) */
  #defaultProductImage = '';
 
  /** @type {Record<string, string>} mapa {cor-armação-slug: url-da-imagem-daquela-cor}, vindo do Liquid (variant.featured_image de cada variante) */
  #variantImages = {};
 
  /** @type {HTMLImageElement | null} preview da armação exibido na tela de cor da lente */
  #previewImageEl = null;
 
  /** @type {string | null} última foto de preview exibida (reaproveitada no resumo) */
  #lastPreviewSrc = null;
 
  /** @type {HTMLElement | null} elemento de preenchimento da barra de progresso */
  #progressFillEl = null;
  #stepRenderedAt = 0;
 
  /** @type {(() => void) | null} remove o listener de scroll da grade de cores, chamado antes de re-renderizar */
  #colorGridScrollCleanup = null;
 
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#interceptClick, { capture: true });
  }
 
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#interceptClick, { capture: true });
  }
 
  /**
   * Intercepta o clique no botão de "adicionar ao carrinho" em capture phase —
   * antes do tema processar o submit E antes de scripts de terceiros (como o
   * listener do app Shop/Shop Pay) que escutam o clique na fase bubble.
   * @param {MouseEvent} event
   */
  #interceptClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const submitButton = target.closest?.('button[type="submit"]');
    if (!submitButton) return;
 
    const form = submitButton.closest('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement)) return;
 
    event.preventDefault();
    event.stopImmediatePropagation();
 
    this.#startFlow(form);
  };
 
  /**
   * @param {HTMLFormElement} form
   */
  #startFlow(form) {
    this.#form = form;
    this.#selections = [];
    this.#addons = [];
    this.#lastPreviewSrc = null;
 
    const productFormComponent = form.closest('product-form-component');
    const tagsAttr = /** @type {HTMLElement | null} */ (productFormComponent)?.dataset.productTags || '';
    const tags = tagsAttr
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .filter(Boolean);
 
    const entryStepId = tags.includes('tipo-solar') ? ENTRY_STEP_BY_TAG['tipo-solar'] : ENTRY_STEP_BY_TAG['tipo-grau'];
 
    this.#mediaList = this.#parseMediaList(
      /** @type {HTMLElement | null} */ (productFormComponent)?.dataset.productMedia || ''
    );
 
    this.#defaultProductImage =
      /** @type {HTMLElement | null} */ (productFormComponent)?.dataset.productImage || this.#mediaList[0]?.src || '';
 
    try {
      this.#variantImages = JSON.parse(
        /** @type {HTMLElement | null} */ (productFormComponent)?.dataset.variantImages || '{}'
      );
    } catch (error) {
      console.error('[lens-selector] erro ao ler o mapa de imagens por cor de armação:', error);
      this.#variantImages = {};
    }
 
    const checkedSwatch = /** @type {HTMLInputElement | null} */ (
      document.querySelector('fieldset.variant-option--swatches input[type="radio"]:checked')
    );
    const frameColorRaw = checkedSwatch?.value || /** @type {HTMLElement | null} */ (productFormComponent)?.dataset.variantTitle || '';
    this.#frameColorSlug = handleize(frameColorRaw);
 
    this.#lensPreviewMap = this.#parseLensPreviewMap();
 
    this.#stack = [entryStepId];
    this.#renderStep(entryStepId);
 
    const dialog = /** @type {any} */ (this.closest('dialog-component'));
    dialog?.showDialog?.();
 
    // Evita que o navegador foque automaticamente o botão de fechar (X) ao
    // abrir a gaveta (comportamento padrão do showModal(), usado internamente
    // pelo dialog-component), o que fazia o X aparecer com destaque de foco,
    // parecendo "pré-selecionado". Usa um pequeno atraso porque o próprio
    // showDialog() pode aplicar o foco dele só depois da animação de abertura.
    const nativeDialog = this.closest('dialog');
    if (nativeDialog) {
      nativeDialog.setAttribute('tabindex', '-1');
      requestAnimationFrame(() => {
        setTimeout(() => {
          nativeDialog.focus();
        }, 50);
      });
    }
  }
 
  /**
   * Lê e decodifica o <script type="application/json" data-lens-preview-map>
   * renderizado pelo Liquid em lens-selector-drawer.liquid.
   * @returns {Record<string, Record<string, string | null>>}
   */
  #parseLensPreviewMap() {
    const script = document.querySelector('script[data-lens-preview-map]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.error('[lens-selector] erro ao ler o mapa de preview de cores:', error);
      return {};
    }
  }
 
  /**
   * Decodifica a lista "alt::url|||alt::url" vinda do data-product-media.
   * @param {string} raw
   * @returns {Array<{alt: string, src: string}>}
   */
  #parseMediaList(raw) {
    if (!raw) return [];
    return raw
      .split('|||')
      .map((pair) => {
        const [alt, src] = pair.split('::');
        return { alt: (alt || '').toLowerCase(), src: src || '' };
      })
      .filter((entry) => entry.src);
  }
 
  /**
   * Garante que a barra de progresso existe no DOM, criando-a antes do
   * corpo do wizard na primeira renderização.
   * @returns {HTMLElement} elemento de preenchimento (fill) da barra
   */
  #ensureProgressBar() {
    if (this.#progressFillEl) return this.#progressFillEl;
 
    const track = document.createElement('div');
    track.className = 'lens-selector__progress-track';
 
    const fill = document.createElement('div');
    fill.className = 'lens-selector__progress-fill';
    track.appendChild(fill);
 
    this.insertAdjacentElement('afterbegin', track);
    this.#progressFillEl = fill;
    return fill;
  }
 
  /**
   * Monta o SVG ilustrativo de uma receita de exemplo, com os campos
   * esférico/cilíndrico/eixo de OD e OE, usado no modal de ajuda das
   * opções de grau.
   * @param {{od: {esf: string, cil: string, eixo: string}, oe: {esf: string, cil: string, eixo: string}}} info
   * @returns {string}
   */
  #buildPrescriptionSVG(info) {
    return `
      <svg viewBox="0 0 300 190" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="exemplo de receita">
        <rect x="0.5" y="0.5" width="299" height="189" rx="10" fill="none" stroke="currentColor" stroke-opacity="0.15" />
        <text x="20" y="28" font-size="11" font-weight="700" fill="currentColor">receita (exemplo)</text>
        <line x1="20" y1="40" x2="280" y2="40" stroke="currentColor" stroke-opacity="0.12" />
 
        <text x="140" y="60" font-size="11" font-weight="600" fill="currentColor" text-anchor="middle">OD</text>
        <text x="220" y="60" font-size="11" font-weight="600" fill="currentColor" text-anchor="middle">OE</text>
 
        <rect x="16" y="68" width="268" height="52" rx="6" fill="#fff3b0" fill-opacity="0.55" />
 
        <text x="24" y="86" font-size="10" fill="currentColor" fill-opacity="0.65">esférico</text>
        <text x="140" y="86" font-size="11" fill="currentColor" text-anchor="middle">${info.od.esf}</text>
        <text x="220" y="86" font-size="11" fill="currentColor" text-anchor="middle">${info.oe.esf}</text>
 
        <text x="24" y="110" font-size="10" fill="currentColor" fill-opacity="0.65">cilíndrico</text>
        <text x="140" y="110" font-size="11" fill="currentColor" text-anchor="middle">${info.od.cil}</text>
        <text x="220" y="110" font-size="11" fill="currentColor" text-anchor="middle">${info.oe.cil}</text>
 
        <text x="24" y="140" font-size="10" fill="currentColor" fill-opacity="0.65">eixo</text>
        <text x="140" y="140" font-size="11" fill="currentColor" text-anchor="middle">${info.od.eixo}</text>
        <text x="220" y="140" font-size="11" fill="currentColor" text-anchor="middle">${info.oe.eixo}</text>
 
        <text x="150" y="170" font-size="9" fill="currentColor" fill-opacity="0.55" text-anchor="middle">
          área destacada = valores que este grau contempla
        </text>
      </svg>
    `;
  }
 
  /**
   * Abre um modal (dialog nativo, empilhado sobre a gaveta do wizard) com a
   * ilustração da receita de exemplo e a legenda da faixa de valores.
   * @param {object} option
   */
  #showPrescriptionInfo(option) {
    const info = option.prescriptionInfo;
    if (!info) return;
 
    const dialog = document.createElement('dialog');
    dialog.className = 'lens-selector__info-dialog';
 
    const header = document.createElement('div');
    header.className = 'lens-selector__info-dialog-header';
 
    const title = document.createElement('p');
    title.className = 'lens-selector__info-dialog-title';
    title.textContent = `como ler sua receita — ${option.label.split('|')[0].trim()}`;
    header.appendChild(title);
 
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'lens-selector__info-dialog-close';
    closeButton.setAttribute('aria-label', 'fechar');
    closeButton.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    closeButton.addEventListener('click', (clickEvent) => {
      clickEvent.stopPropagation();
      dialog.close();
    });
    header.appendChild(closeButton);
 
    const bodyEl = document.createElement('div');
    bodyEl.className = 'lens-selector__info-dialog-body';
    bodyEl.innerHTML = this.#buildPrescriptionSVG(info);
 
    const caption = document.createElement('p');
    caption.className = 'lens-selector__info-dialog-caption';
    caption.textContent = info.caption;
    bodyEl.appendChild(caption);
 
    dialog.append(header, bodyEl);
 
    // Fecha ao clicar no backdrop (fora do conteúdo do dialog).
    dialog.addEventListener('click', (clickEvent) => {
      if (clickEvent.target === dialog) dialog.close();
    });
 
    // Remove o elemento do DOM assim que fechar, em vez de deixar acumulando.
    dialog.addEventListener('close', () => {
      dialog.remove();
    });
 
    this.appendChild(dialog);
    dialog.showModal();
 
    // Evita que o navegador foque automaticamente o botão de fechar ao abrir
    // (comportamento padrão do showModal()), o que fazia o X aparecer com
    // destaque de foco, parecendo "pré-selecionado".
    dialog.setAttribute('tabindex', '-1');
    dialog.focus();
  }
 
  /**
   * Atualiza a largura da barra de progresso de acordo com a profundidade
   * atual da pilha de navegação. Só chega a 100% na etapa de resumo.
   * @param {string} stepId
   */
  #updateProgress(stepId) {
    const fill = this.#ensureProgressBar();
    const step = ALL_STEPS[stepId];
    const percent = step?.summary ? 100 : Math.min(this.#stack.length * 22, 90);
    fill.style.width = `${percent}%`;
  }
 
  /**
   * Ignora cliques que cheguem muito perto da troca de etapa (menos de
   * ~350ms), prevenindo cliques fantasma comuns em mobile.
   * @returns {boolean}
   */
  #isGhostClick() {
    return performance.now() - this.#stepRenderedAt < 350;
  }
 
  /**
   * Bloqueia toques nos elementos interativos de um container por um breve
   * instante (mesma janela do #isGhostClick). Evita que o navegador "herde"
   * hover/toque no mesmo ponto da tela onde o usuário acabou de tocar na
   * etapa anterior — comportamento comum no iOS Safari, que pode fazer um
   * botão da nova etapa aparecer com aparência de "pré-selecionado".
   * @param {HTMLElement} container
   */
  #blockInteractionBriefly(container) {
    const interactiveEls = container.querySelectorAll('button');
    interactiveEls.forEach((el) => {
      el.style.pointerEvents = 'none';
    });
    setTimeout(() => {
      interactiveEls.forEach((el) => {
        el.style.pointerEvents = '';
      });
    }, 350);
  }
 
  /**
   * @param {string} stepId
   */
  #renderStep(stepId) {
    const step = ALL_STEPS[stepId];
    if (!step) return;
 
    this.#updateProgress(stepId);
    this.#stepRenderedAt = performance.now();
 
    this.refs.title.textContent = step.title || 'escolha seu tipo de lente';
    this.refs.backButton.hidden = this.#stack.length <= 1;
 
    const body = this.refs.body;
    body.replaceChildren();
 
    this.refs.previewArea.replaceChildren();
    this.refs.previewArea.hidden = true;
 
    this.refs.footerArea.replaceChildren();
    this.refs.footerArea.hidden = true;
 
    if (this.#colorGridScrollCleanup) {
      this.#colorGridScrollCleanup();
      this.#colorGridScrollCleanup = null;
    }
 
    if (step.placeholder) {
      const message = document.createElement('p');
      message.className = 'lens-selector__placeholder';
      message.textContent = step.message;
      body.appendChild(message);
      return;
    }
 
    if (step.subtitle) {
      const subtitle = document.createElement('p');
      subtitle.className = 'lens-selector__subtitle';
      subtitle.textContent = step.subtitle;
      body.appendChild(subtitle);
    }
 
    if (step.colorPicker) {
      this.#renderColorPicker(body, step);
      this.#blockInteractionBriefly(body);
      return;
    }
 
    if (step.summary) {
      this.#renderSummary(body);
      this.#blockInteractionBriefly(body);
      return;
    }
 
    for (const option of step.options) {
      const wrapper = document.createElement('div');
      wrapper.className = 'lens-selector__option';
 
      const row = document.createElement('div');
      row.className = 'lens-selector__option-row';
 
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lens-selector__option-button';
      button.textContent = option.label;
      button.addEventListener('click', (clickEvent) => {
        clickEvent.stopPropagation();
        if (this.#isGhostClick()) return;
        this.#selectOption(step, option);
      });
 
      if (option.prescriptionInfo) {
        // Agrupa botão + (i) numa div à esquerda, pra que o ícone fique
        // colado ao botão em vez de disputar espaço com o preço — isso
        // também dá mais largura pro texto do botão, evitando quebra de linha.
        const buttonGroup = document.createElement('div');
        buttonGroup.className = 'lens-selector__option-button-group';
        buttonGroup.appendChild(button);
 
        const infoButton = document.createElement('button');
        infoButton.type = 'button';
        infoButton.className = 'lens-selector__option-info';
        infoButton.innerHTML =
          '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
          '<path d="M7.00033 12.8333C10.222 12.8333 12.8337 10.2217 12.8337 7.00001C12.8337 3.77834 10.222 1.16667 7.00033 1.16667C3.77866 1.16667 1.16699 3.77834 1.16699 7.00001C1.16699 10.2217 3.77866 12.8333 7.00033 12.8333Z" class="lens-selector__option-info-circle" stroke-linecap="round" stroke-linejoin="round"/>' +
          '<path d="M7 9.33333V7" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
          '<path d="M7 4.66667H7.00625" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg>';
        infoButton.setAttribute('aria-label', `ver exemplo de receita para ${option.label}`);
        infoButton.addEventListener('click', (clickEvent) => {
          clickEvent.stopPropagation();
          this.#showPrescriptionInfo(option);
        });
        buttonGroup.appendChild(infoButton);
 
        row.appendChild(buttonGroup);
      } else {
        row.appendChild(button);
      }
 
      if (option.price) {
        const price = document.createElement('span');
        price.className = 'lens-selector__option-price';
        price.textContent = option.price;
        row.appendChild(price);
      }
 
      const description = document.createElement('p');
      description.className = 'lens-selector__option-description';
      description.textContent = option.description;
 
      wrapper.append(row, description);
      body.appendChild(wrapper);
    }
 
    if (step.banner) {
      const banner = document.createElement('div');
      banner.className = 'lens-selector__banner';
 
      const icon = document.createElement('div');
      icon.className = 'lens-selector__banner-icon';
      icon.textContent = '📱';
 
      const title = document.createElement('p');
      title.className = 'lens-selector__banner-title';
      title.textContent = step.banner.title;
 
      const body_ = document.createElement('p');
      body_.className = 'lens-selector__banner-body';
      body_.textContent = step.banner.body;
 
      banner.append(icon, title, body_);
      body.appendChild(banner);
    }
 
    this.#blockInteractionBriefly(body);
  }
 
  /**
   * Aumenta a resolução de uma URL de imagem do CDN da Shopify, sobrescrevendo
   * (ou adicionando) o parâmetro "width" — evita imagens borradas quando a
   * origem é um thumbnail pequeno exibido em tamanho maior no resumo.
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  #upscaleImageUrl(url, width = 800) {
    if (!url) return url;
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch {
      return url;
    }
  }
 
  /**
   * Renderiza o resumo final: foto + duas colunas (produto/tipo/cor à
   * esquerda, lentes escolhidas à direita), e o botão que adiciona ao carrinho.
   * @param {HTMLElement} body
   */
  #renderSummary(body) {
    const productFormComponent = this.#form?.closest('product-form-component');
    const image = this.#lastPreviewSrc || /** @type {HTMLElement | null} */ (productFormComponent)?.dataset.productImage;
    const title = /** @type {HTMLElement | null} */ (productFormComponent)?.dataset.productTitle || '';
    const variantTitle = /** @type {HTMLElement | null} */ (productFormComponent)?.dataset.variantTitle || '';
    const lensType = this.#selections[0]?.label || '';
 
    const card = document.createElement('div');
    card.className = 'lens-selector__summary-card';
 
    if (image) {
      const img = document.createElement('img');
      img.src = this.#upscaleImageUrl(image, 800);
      img.alt = title;
      img.className = 'lens-selector__summary-image';
      card.appendChild(img);
    }
 
    const columns = document.createElement('div');
    columns.className = 'lens-selector__summary-columns';
 
    // Coluna esquerda: nome do produto + tipo de lente, e cor da armação
    const infoColumn = document.createElement('div');
    infoColumn.className = 'lens-selector__summary-column lens-selector__summary-column--info';
 
    const titleEl = document.createElement('p');
    titleEl.className = 'lens-selector__summary-title';
 
    const titleName = document.createElement('span');
    titleName.className = 'lens-selector__summary-title-name';
    titleName.textContent = title;
    titleEl.appendChild(titleName);
 
    if (lensType) {
      const titleType = document.createElement('span');
      titleType.className = 'lens-selector__summary-title-type';
      titleType.textContent = ` | ${lensType}`;
      titleEl.appendChild(titleType);
    }
 
    infoColumn.appendChild(titleEl);
 
    if (variantTitle) {
      const variantEl = document.createElement('p');
      variantEl.className = 'lens-selector__summary-variant';
      variantEl.textContent = variantTitle;
      infoColumn.appendChild(variantEl);
    }
 
    // Coluna direita: label "lentes" + demais seleções (grau, tratamento, cor da lente etc.)
    // Só renderiza se houver alguma seleção além da primeira (tipo de lente),
    // já que nos fluxos "sem grau" não existe nada além disso.
    const extraSelections = this.#selections.slice(1);
 
    columns.appendChild(infoColumn);
 
    if (extraSelections.length > 0) {
      const lensesColumn = document.createElement('div');
      lensesColumn.className = 'lens-selector__summary-column lens-selector__summary-column--lenses';
 
      const lensesLabel = document.createElement('p');
      lensesLabel.className = 'lens-selector__summary-lenses-label';
      lensesLabel.textContent = 'lentes';
      lensesColumn.appendChild(lensesLabel);
 
      for (const selection of extraSelections) {
        const lensLine = document.createElement('p');
        lensLine.className = 'lens-selector__summary-lens-line';
        lensLine.textContent = selection.label;
        lensesColumn.appendChild(lensLine);
      }
 
      columns.appendChild(lensesColumn);
    }
 
    card.appendChild(columns);
    body.appendChild(card);
 
    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.className = 'lens-selector__confirm-button';
    confirmButton.textContent = 'ir para carrinho';
    confirmButton.addEventListener('click', (clickEvent) => {
      clickEvent.stopPropagation();
      this.#confirmSelection();
    });
    body.appendChild(confirmButton);
  }
 
  /**
   * Devolve o background CSS (cor sólida ou gradiente) usado na bolinha do
   * botão de cor, a partir da colorKey (ex: "marrom-ll-degrade").
   * @param {string} colorKey
   * @returns {string}
   */
  #swatchBackground(colorKey) {
    const isDegrade = colorKey.endsWith('-degrade');
    const base = isDegrade ? colorKey.slice(0, -'-degrade'.length) : colorKey;
 
    if (isDegrade) {
      const stops = LENS_COLOR_GRADIENT[base];
      return stops ? `linear-gradient(180deg, ${stops[0]}, ${stops[1]})` : '#cccccc';
    }
 
    return LENS_COLOR_HEX[base] || '#cccccc';
  }
 
  /**
   * Renderiza a etapa de cor da lente: foto da armação no topo (que troca
   * conforme a cor clicada), grade de cores, e botão de confirmar no final.
   * @param {HTMLElement} body
   * @param {object} step
   */
  #renderColorPicker(body, step) {
    const img = document.createElement('img');
    img.className = 'lens-selector__frame-preview';
    img.alt = '';
    const framePreviewImage = this.#variantImages[this.#frameColorSlug];
    img.src = this.#upscaleImageUrl(framePreviewImage || this.#defaultProductImage || this.#mediaList[0]?.src || '', 800);
    this.refs.previewArea.appendChild(img);
    this.refs.previewArea.hidden = false;
    this.#previewImageEl = img;
 
    this.#preloadLensImages(step);
 
    /** @type {object | null} */
    let pendingOption = null;
 
    const grid = document.createElement('div');
    grid.className = 'lens-selector__color-grid';
 
    // Enquanto o wizard rola (arrastar pra ver mais cores), remove
    // pointer-events dos botões por completo — assim nenhum estado visual
    // de hover/active/tap-highlight consegue aparecer neles durante o
    // gesto, independente da causa exata (comportamento varia por
    // navegador/SO). ~150ms depois que o scroll parar, volta ao normal.
    let scrollEndTimeout;
    const handleGridScroll = () => {
      grid.classList.add('lens-selector__color-grid--scrolling');
      clearTimeout(scrollEndTimeout);
      scrollEndTimeout = setTimeout(() => {
        grid.classList.remove('lens-selector__color-grid--scrolling');
      }, 150);
    };
    this.refs.body.addEventListener('scroll', handleGridScroll, { passive: true });
    this.#colorGridScrollCleanup = () => {
      this.refs.body.removeEventListener('scroll', handleGridScroll);
      clearTimeout(scrollEndTimeout);
    };
 
    const swatchButtons = [];
 
    for (const option of step.options) {
      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = 'lens-selector__color-swatch';
 
      if (option.colorKey) {
        const dot = document.createElement('span');
        dot.className = 'lens-selector__color-swatch-dot';
        dot.style.background = this.#swatchBackground(option.colorKey);
        swatch.appendChild(dot);
      }
 
      const labelEl = document.createElement('span');
      labelEl.className = 'lens-selector__color-swatch-label';
      labelEl.textContent = option.label;
      swatch.appendChild(labelEl);
 
      if (option.price) {
        const price = document.createElement('span');
        price.className = 'lens-selector__color-swatch-price';
        price.textContent = option.price;
        swatch.appendChild(price);
      }
 
      swatch.addEventListener('click', (clickEvent) => {
        clickEvent.stopPropagation();
 
        for (const btn of swatchButtons) {
          btn.classList.remove('lens-selector__color-swatch--selected');
          btn.style.backgroundColor = '';
          btn.style.color = '';
          btn.style.borderColor = '';
        }
 
        swatch.classList.add('lens-selector__color-swatch--selected');
        swatch.style.backgroundColor = 'var(--color-foreground)';
        swatch.style.color = 'var(--color-background)';
        swatch.style.borderColor = 'var(--color-foreground)';
 
        pendingOption = option;
        confirmButton.disabled = false;
 
        if (option.colorKey) {
          this.#updatePreviewImage(option.colorKey);
        }
      });
 
      swatchButtons.push(swatch);
      grid.appendChild(swatch);
    }
 
    body.appendChild(grid);
 
    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.className = 'lens-selector__confirm-button';
    confirmButton.textContent = 'confirmar escolha';
    confirmButton.disabled = true;
    confirmButton.addEventListener('click', (clickEvent) => {
      clickEvent.stopPropagation();
      if (pendingOption) {
        this.#selectOption(step, pendingOption);
      }
    });
    this.refs.footerArea.appendChild(confirmButton);
    this.refs.footerArea.hidden = false;
  }
 
  /**
   * Baixa em segundo plano (sem inserir no DOM) todas as fotos de cor de
   * lente disponíveis para a armação atualmente selecionada, aproveitando
   * o tempo em que o cliente está lendo as opções. Assim, quando ele
   * clica numa cor, a imagem já está no cache do navegador e troca sem
   * demora perceptível.
   * @param {object} step
   */
  #preloadLensImages(step) {
    const framePreviews = this.#lensPreviewMap[this.#frameColorSlug];
    if (!framePreviews) return;
 
    for (const option of step.options) {
      const src = option.colorKey ? framePreviews[option.colorKey] : null;
      if (!src) continue;
      const preloadImg = new Image();
      preloadImg.src = src;
    }
  }
 
  /**
   * Busca a URL de preview (armação + cor de lente) no mapa gerado pelo
   * Liquid, usando a cor da armação atual + a cor de lente clicada.
   * @param {string} colorKey
   * @returns {string | null}
   */
  #findPreviewImage(colorKey) {
    const framePreviews = this.#lensPreviewMap[this.#frameColorSlug];
    const src = framePreviews ? framePreviews[colorKey] : null;
 
    if (!src) {
      console.warn(
        `[lens-selector] preview não encontrado para armação "${this.#frameColorSlug}" + lente "${colorKey}". Confira se o arquivo foi enviado em Conteúdo > Arquivos com o nome exato.`
      );
    }
 
    return src || null;
  }
 
  /**
   * Atualiza a foto de preview pra mostrar a cor de lente selecionada.
   * @param {string} colorKey
   */
  #updatePreviewImage(colorKey) {
    if (!this.#previewImageEl) return;
    const src = this.#findPreviewImage(colorKey);
    if (src) {
      this.#previewImageEl.src = src;
      this.#lastPreviewSrc = src;
    }
  }
 
  /**
   * @param {object} step
   * @param {object} option
   */
  #selectOption(step, option) {
    this.#selections.push({ key: step.propertyKey, label: option.label });
 
    const addonVariantId = ADDON_VARIANTS[option.id];
    this.#addons.push(addonVariantId ? { variantId: addonVariantId, label: option.label } : null);
 
    if (option.type === 'terminal') {
      this.#confirmSelection();
      return;
    }
 
    if (option.type === 'next' && ALL_STEPS[option.next]) {
      this.#stack.push(option.next);
      this.#renderStep(option.next);
    }
  }
 
  /**
   * Volta para a etapa anterior na pilha de navegação.
   */
  goBack() {
    if (this.#stack.length <= 1) return;
 
    // Remove a seleção (e o addon, se houver) feitos na etapa que está sendo abandonada.
    this.#selections.pop();
    this.#addons.pop();
 
    this.#stack.pop();
    this.#renderStep(this.#stack[this.#stack.length - 1]);
  }
 
  /**
   * Fecha a gaveta delegando para o <dialog-component> pai.
   */
  closeDialog() {
    const dialog = /** @type {any} */ (this.closest('dialog-component'));
    dialog?.closeDialog?.();
  }
 
  /**
   * Monta a propriedade combinada de todas as seleções e envia a armação +
   * complementos pagos como múltiplos itens, via fetch (sem passar pelo
   * fluxo nativo do tema), navegando pra página /cart no sucesso.
   */
  #confirmSelection() {
    if (!this.#form) return;
 
    const formData = new FormData(this.#form);
    const mainVariantId = formData.get('id');
    const quantity = Number(formData.get('quantity')) || 1;
 
    /** @type {Record<string, string>} */
    const properties = {};
    for (const selection of this.#selections) {
      properties[selection.key] = selection.label;
    }
 
    if (this.#lastPreviewSrc) {
      properties['_preview_image'] = this.#lastPreviewSrc;
    }
 
    // ID único para linking correto entre armação e upgrades
    // Garante que duas armações iguais tenham grupos separados
    const groupId = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7);
 
    // Grava o groupId na armação para matching com os upgrades
    properties['_group_id'] = groupId;
 
    const items = [
      {
        id: Number(mainVariantId),
        quantity,
        properties,
      },
    ];
 
    for (const addon of this.#addons) {
      if (!addon) continue;
      items.push({
        id: Number(addon.variantId),
        quantity,
        properties: {
          'upgrade para': properties['tipo de lente'] || '',
          '_armacao_key': groupId,
        },
      });
    }
 
    this.closeDialog();
 
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error('[lens-selector] erro ao adicionar ao carrinho:', response.message);
          return;
        }
 
        const cartRoot = /** @type {any} */ (window).Shopify?.routes?.root || '/';
        window.location.href = `${cartRoot.replace(/\/$/, '')}/cart`;
      })
      .catch((error) => {
        console.error('[lens-selector] erro ao adicionar ao carrinho:', error);
      });
  }
}
 
if (!customElements.get('lens-selector-component')) {
  customElements.define('lens-selector-component', LensSelectorComponent);
}
 